/**
 * PresetTemplates (iter-44) — Library-level surface for managing
 * Publisher's Kits (internal data model: preset_kits) outside a book.
 *
 * The kit editor UI (`PublishersKitBar` + `BookInfoPresetControls`)
 * previously lived only inside the Manual Compose modal, meaning
 * authors had to open a book to create a reusable preset. This page
 * exposes the SAME components with a fresh top-level chrome so the
 * whole CRUD flow (list / create / edit / delete / rename) is
 * available from the Library nav.
 *
 * Applying a kit is still done from the book editor (a kit needs a
 * target book to write into). This page focuses on managing the
 * library of kits + section/info presets.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import { PublishersKitBar, BookInfoPresetControls, BOOK_INFO_FIELDS } from "@/components/manual/PublishersKit";
import { SectionPresetEditor } from "@/components/manual/SectionPresetEditor";
import { PresetDataBrowser } from "@/components/manual/PresetDataBrowser";
import { KitsManager } from "@/components/manual/KitsManager";

const EMPTY_INFO = Object.fromEntries(BOOK_INFO_FIELDS.map((f) => [f.key, ""]));

export default function PresetTemplates() {
  const { user, loading } = useAuth();
  const [kits, setKits] = useState([]);
  const [presets, setPresets] = useState([]);
  const [team, setTeam] = useState(null);
  // iter-99 — Workspaces the caller can share kits into. Even users
  // without an active ph360 team can share via a workspace (which is
  // where VAs, editors, and clients typically live).
  const [workspaces, setWorkspaces] = useState([]);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const [kitR, prR, teamR, wsR] = await Promise.all([
        api.get("/preset-kits"),
        api.get("/manual-presets"),
        api.get("/ph360/team").catch(() => ({ data: null })),
        api.get("/workspaces/mine").catch(() => ({ data: null })),
      ]);
      setKits(kitR.data || []);
      setPresets(prR.data || []);
      // `/ph360/team` returns `{ team: { id, name, ... } | null }` — unwrap
      // the inner team object so callers can read `team.id` directly.
      setTeam(teamR.data?.team || null);
      // `/workspaces/mine` returns either an array or `{workspaces: [...]}`
      // — coerce to a plain array of `{id, name, ...}` regardless.
      const wsList = Array.isArray(wsR.data)
          ? wsR.data
          : (wsR.data?.workspaces || []);
      setWorkspaces(wsList);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { if (user) reload(); }, [user, reload]);

  // Kit CRUD ---------------------------------------------------------------
  const createKit = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/preset-kits", payload);
      setKits((prev) => [data, ...prev]);
      toast.success(`Created kit "${data.name}"`);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  }, []);
  const updateKit = useCallback(async (kitId, payload) => {
    try {
      const { data } = await api.patch(`/preset-kits/${kitId}`, payload);
      setKits((prev) => prev.map((k) => k.id === kitId ? data : k));
      toast.success(`Updated "${data.name}"`);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  }, []);
  const deleteKit = useCallback(async (kitId) => {
    const k = kits.find((x) => x.id === kitId);
    const label = k ? `"${k.name}"` : "this kit";
    // iter-95c — fetch "where used" before confirming so authors know
    // exactly which books this kit was applied to. The kit content is
    // already copied into each book at apply time, so deleting the
    // kit does NOT remove the applied content — but it does break any
    // "Rebuild" workflow that relied on the kit. Surface the list so
    // the author can decide.
    let usageLine = "";
    try {
      const { data } = await api.get(`/preset-kits/${kitId}/usage`);
      const n = data.count || 0;
      if (n > 0) {
        const sample = (data.books || []).slice(0, 6).map((b) => `• ${b.title}`).join("\n");
        const more = n > 6 ? `\n(…and ${n - 6} more)` : "";
        usageLine = `\n\nApplied to ${n} book${n === 1 ? "" : "s"}:\n${sample}${more}\n\nNote: their front/back matter, TOC note and intro copy stay in place — this only removes the reusable kit itself.`;
      }
    } catch (_) { /* non-fatal — fall through to plain confirm */ }
    const msg = `Delete kit ${label}? This does not remove the individual presets it references.${usageLine}`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/preset-kits/${kitId}`);
      setKits((prev) => prev.filter((k) => k.id !== kitId));
      toast.success("Kit deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  }, [kits]);
  // iter-95b — quick single-field rename. Uses the same PATCH as
  // updateKit but keeps a lighter UX than opening the full modal.
  const renameKit = useCallback(async (kitId, nextName) => {
    const name = (nextName || "").trim();
    if (!name) return null;
    try {
      const { data } = await api.patch(`/preset-kits/${kitId}`, { name });
      setKits((prev) => prev.map((k) => k.id === kitId ? data : k));
      toast.success(`Renamed to "${data.name}"`);
      return data;
    } catch (err) { toast.error(formatApiError(err)); return null; }
  }, []);

  // Preset CRUD helpers used by BookInfoPresetControls --------------------
  const saveInfoPreset = useCallback(async (name, values) => {
    try {
      const { data } = await api.post("/manual-presets",
        { name, kind: "info", fields: values });
      setPresets((prev) => [data, ...prev]);
      toast.success(`Saved info preset "${data.name}"`);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  const deletePreset = useCallback(async (id) => {
    if (!window.confirm("Delete this preset?")) return;
    try {
      await api.delete(`/manual-presets/${id}`);
      setPresets((prev) => prev.filter((p) => p.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);

  if (loading) return <div className="p-8 text-[color:var(--text-muted)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="preset-templates-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">§ Library</p>
            <h1 className="font-editorial text-3xl mt-1">Preset Templates</h1>
            <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-xl">
              Reusable bundles of front / back matter, book-info defaults and TOC/intro copy.
              Great for genre boilerplate: <em>Medical Disclaimer + RN Credentials</em>,
              <em> Financial Disclaimer + CFP Credentials</em>, standard imprint copyright, and so on.
              Apply any template to a book from the book editor's <strong>Publisher's Kit</strong> bar.
            </p>
          </div>
          <Link to="/dashboard" className="btn-ghost text-xs" data-testid="pt-back-dashboard">← Dashboard</Link>
        </div>

        <div className="mt-8 space-y-8">
          {busy && kits.length === 0 && (
            <p className="text-sm text-[color:var(--text-muted)]">Loading templates…</p>
          )}
          <PublishersKitBar
            kits={kits}
            presets={presets}
            team={team}
            workspaces={workspaces}
            currentTocNote=""
            currentIntroduction=""
            /* Apply is intentionally a no-op here — this page manages the
               library, not any single book. Authors apply from a book. */
            onApplyKit={() => toast.info("Open a book, then use its Publisher's Kit bar to apply this template.")}
            onDeleteKit={deleteKit}
            onCreateKit={createKit}
            onUpdateKit={updateKit}
          />

          {/* iter-95b — dedicated Kits list with expand-to-see-presets,
              rename, edit and delete. Complements the compact Publisher's
              Kit bar above (which is the quick apply/create surface). */}
          <KitsManager
            kits={kits}
            presets={presets}
            team={team}
            workspaces={workspaces}
            onRenameKit={renameKit}
            onUpdateKit={updateKit}
            onDeleteKit={deleteKit}
            onKitsChanged={reload}
          />

          <PresetDataBrowser presets={presets} />

          <SectionPresetEditor
            presets={presets}
            team={team}
            workspaces={workspaces}
            onCreated={(p) => setPresets((prev) => [p, ...prev])}
            onDeleted={(id) => setPresets((prev) => prev.filter((p) => p.id !== id))}
          />

          <section className="border border-[color:var(--border)] bg-white p-4" data-testid="pt-info-presets-card">
            <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Book-info presets</p>
            <p className="text-xs text-[color:var(--text-muted)] mt-1">
              Publisher / imprint / author line / credentials — save once, drop into any book.
            </p>
            <div className="mt-3">
              <BookInfoPresetControls
                presets={presets.filter((p) => p.kind === "info")}
                currentInfo={EMPTY_INFO}
                onSave={saveInfoPreset}
                onLoad={() => toast.info("Open a book to load a preset into its info fields.")}
                onDelete={deletePreset}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
